import { getRovoParams } from '@atlaskit/rovo-triggers';
import { useEffect } from 'react';
import { createResource, useResource, type UseResourceHookResponse } from 'react-resource-router/resources';
import { createActionsHook, createStateHook, createStore, type Action } from 'react-sweet-state';

import { logMessage } from '@townsquare/logging';
import type { HomeResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';

export type RovoEntitlement = {
	isRovoEnabled: boolean;
	isRovoLLMEnabled: boolean;
	reason: string | null;
};

type EntitlementV2APIResponse = {
	isRovoEnabled: boolean;
	isRovoLLMEnabled: boolean;
	reason: string | null;
};

type AppAIConfigState = {
	isRovoDisabledInApp: boolean;
};

const appAIConfigStore = createStore({
	initialState: {
		isRovoDisabledInApp: false,
	},
	actions: {
		setIsRovoDisabledInApp:
			(isRovoDisabledInApp: boolean): Action<AppAIConfigState> =>
			({ setState }) => {
				setState({ isRovoDisabledInApp });
			},
	},
});

const useAppAIConfigActions = createActionsHook(appAIConfigStore);
const useAppAIConfig = createStateHook(appAIConfigStore);

/**
 * Should only be called once per app as conflicting writes may cause weird behaviour.
 *
 * Setting true will disable Rovo touchpoints in the app. Calling with false will be a no-op, users will still need entitlement permissions
 * to access Rovo, so if you are still loading the permission check, call this with false.
 */
export const useSetAIDisabledInApp = (isRovoDisabledInApp: boolean) => {
	const { setIsRovoDisabledInApp } = useAppAIConfigActions();

	useEffect(() => {
		setIsRovoDisabledInApp(isRovoDisabledInApp);
	}, [isRovoDisabledInApp, setIsRovoDisabledInApp]);
};

export const getTraceIdFromResponse = (response: Response | undefined) =>
	response?.headers?.get('x-trace-id') ??
	response?.headers?.get('atl-traceid') ??
	response?.headers?.get('x-b3-traceid') ??
	undefined;

const getRovoEntitlement = async (cloudId: string) => {
	try {
		const response = await fetch('/gateway/api/assist/rovo/v2/entitlement', {
			headers: {
				'X-Cloudid': cloudId ?? '',
			},
		});

		if (!response.ok) {
			const responseErrorMessage = await response.text().catch(() => undefined);
			logMessage('Failed to fetch RovoEntitlement [response not ok]', 'error', {
				cloudId,
				responseStatus: response.status,
				responseTraceId: getTraceIdFromResponse(response),
				responseErrorMessage,
			});

			return {
				isRovoEnabled: false,
				isRovoLLMEnabled: false,
				reason: 'Failed to fetch RovoEntitlement [response not ok]',
			};
		}

		const data: EntitlementV2APIResponse = await response.json();

		return {
			isRovoEnabled: data.isRovoEnabled,
			isRovoLLMEnabled: data.isRovoLLMEnabled,
			reason: data.reason,
		};
	} catch (error) {
		logMessage('Failed to fetch RovoEntitlement [catch block]', 'error', {
			cloudId,
			error,
		});

		return {
			isRovoEnabled: false,
			isRovoLLMEnabled: false,
			reason: 'Failed to fetch RovoEntitlement [catch block]',
		};
	}
};

export const fetchRovoEntitlement = async (cloudId: string | undefined | null) => {
	if (!cloudId) {
		return {
			isRovoEnabled: false,
			isRovoLLMEnabled: false,
			reason: 'No cloudId exists',
		};
	}

	return await getRovoEntitlement(cloudId);
};

type RovoEntitlementResponse = Pick<UseResourceHookResponse<RovoEntitlement>, 'loading' | 'data' | 'error'>;

export const useRovoEntitlement = (): RovoEntitlementResponse => {
	const actualResource = useResource(rovoEntitlementResource);
	const { isRovoDisabledInApp } = useAppAIConfig();

	if (!fg('townsquare_enable_rbac_in_entitlement_check')) {
		return actualResource;
	}

	if (isRovoDisabledInApp) {
		return {
			loading: false,
			data: {
				isRovoEnabled: false,
				isRovoLLMEnabled: false,
				reason: 'Rovo is disabled due to app permissions',
			},
			error: null,
		};
	}

	return actualResource;
};

export const rovoEntitlementResource = createResource<RovoEntitlement>({
	getData: async (_, resourceContext) => {
		const context = resourceContext as HomeResourceContext;
		const rovoParams = getRovoParams();
		// Workspace doesn't always exist on Atlassian Home **TODAY**, if we are rendering chat without a workspace, we need to default to the first cloudId (as they may still have sites)
		const cloudId = rovoParams.cloudId ?? context.collabContext?.rovoWorkspace;

		return await fetchRovoEntitlement(cloudId);
	},
	getKey: () => 'rovo-entitlement-resource-key',
	type: 'ROVO_ENTITLEMENT',
	maxAge: 60_000 * 30, // 30 minutes cache during the SPA session
});
